import React, {Component} from 'react';
import PropTypes from 'prop-types';
import Unveil from '../Unveil/Unveil';
import {LoadingOverlay} from '@homeaway/react-loading-overlay';
import './UnveilWithAsync.less';

/* istanbul ignore next */
const noop = () => {};

class UnveilWithAsync extends Component {
    static propTypes = {
        /** Name of component/element that will be asynchronously loaded. */
        importComponent: PropTypes.func.isRequired,
        /** Function to import the package.*/
        importFunc: PropTypes.func.isRequired,
        /** Module name if the package contains multiple exports and the module you would like to import is not the default. */
        importModule: PropTypes.string,
        /** Optional callback when an error occurs while attempting to import the package, passes the error object. */
        onImportError: PropTypes.func,
        /** Function to call if the component is in view. */
        onUnveil: PropTypes.func,
        /** Height of the loading container used to fill the space while the element is loaded; specified in pixels. */
        placeholderHeight: PropTypes.number,
        /** The amount of pixels before the viewport that the element should start loading. */
        threshold: PropTypes.number
    };

    static defaultProps = {
        importModule: 'default',
        onImportError: noop,
        onUnveil: noop,
        placeholderHeight: 40
    };

    constructor(props) {
        super(props);

        this._isMounted = false;

        this.state = {
            unveiled: false,
            component: () => null
        };
    }

    componentDidMount() {
        this._isMounted = true;
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    setStateSafe = (newState) => {
        // Defend against error: Can't call setState (or forceUpdate) on an unmounted component.
        if (this._isMounted) {
            this.setState(newState);
        }
    };

    onUnveil = () => {
        const {importFunc, importModule, onImportError, onUnveil} = this.props;

        importFunc()
            .then((result) => {
                this.setStateSafe({
                    component: result[importModule],
                    unveiled: true
                });
            })
            .catch((error) => {
                onImportError(error);
            });

        onUnveil();
    };

    recursivelyReturnChildren = (children) => {
        return React.Children.map(children, (child) => {
            if (!React.isValidElement(child)) {
                return child;
            }

            if (child.props.children) {
                child = React.cloneElement(child, {
                    children: this.recursivelyReturnChildren(
                        child.props.children
                    )
                });
            }

            // if child being rendered matches the importComponent prop, replace the null component with the imported one
            if (
                child.type.toString() === this.props.importComponent.toString()
            ) {
                return <this.state.component {...child.props} />;
            }

            // otherwise return the child itself
            return child;
        });
    };

    /* eslint-disable no-unused-vars */
    render() {
        // strip off any props not applicable to <Unveil> before passing ...rest to <Unveil>
        const {
            children,
            importComponent,
            importFunc,
            importModule,
            onUnveil,
            placeholderHeight,
            ...rest
        } = this.props;

        const placeholderStyles = {
            height: `${placeholderHeight}px`
        };

        return (
            <React.Fragment>
                <Unveil
                    onUnveil={this.onUnveil}
                    unveiled={this.state.unveiled}
                    {...rest}
                >
                    {this.state.unveiled &&
                        this.recursivelyReturnChildren(children)}
                </Unveil>

                {!this.state.unveiled && placeholderHeight && (
                    <div
                        className="UnveilWithAsync__loading-wrapper"
                        style={placeholderStyles}
                    >
                        <LoadingOverlay />
                    </div>
                )}
            </React.Fragment>
        );
    }
}

export default UnveilWithAsync;
